import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useLists, useMe } from '../api/queries';
import { initialOf } from '../api/format';
import type { ListSummary } from '../api/types';

/**
 * Desktop chrome: 60px header, 280px sidebar, 720px content column.
 * The sidebar is always mounted, which is what makes the overview route a
 * sidebar-plus-detail view rather than its own page.
 */
export function DesktopShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { listId } = useParams();
  const { data: lists } = useLists();
  const { data: me } = useMe();

  const owned = (lists ?? []).filter((l) => l.role === 'owner');
  const shared = (lists ?? []).filter((l) => l.role !== 'owner');
  const activeId = Number(listId) || (pathname === '/' ? (lists?.[0]?.id ?? null) : null);

  return (
    <div className="desktop">
      <header className="desktop__header">
        <div className="desktop__brand">Lists</div>
        <div className="desktop__headerMain">
          <div className="desktop__headerCenter">
            <button type="button" className="desktop__search" onClick={() => navigate('/search')}>
              <span aria-hidden="true">⌕</span>
              Search all lists
            </button>
          </div>
          <div className="desktop__headerRight">
            <button type="button" className="primary-button" onClick={() => navigate('/new')}>
              + New list
            </button>
            <button
              type="button"
              className="avatar avatar--header"
              aria-label="Settings"
              onClick={() => navigate('/settings')}
            >
              {initialOf(me?.name)}
            </button>
          </div>
        </div>
      </header>

      <div className="desktop__body">
        <nav className="desktop__sidebar" aria-label="Your lists">
          <div className="sidebar__heading">Yours</div>
          {owned.map((list) => (
            <SidebarRow key={list.id} list={list} active={list.id === activeId} />
          ))}
          <div className="sidebar__heading sidebar__heading--spaced">Shared with you</div>
          {shared.map((list) => (
            <SidebarRow key={list.id} list={list} active={list.id === activeId} />
          ))}
        </nav>
        <main className="desktop__pane">{children}</main>
      </div>
    </div>
  );
}

function SidebarRow({ list, active }: { list: ListSummary; active: boolean }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className={active ? 'sidebar__row is-active' : 'sidebar__row'}
      onClick={() => navigate(`/lists/${list.id}`)}
    >
      <span className="sidebar__name">{list.name}</span>
      <span className="sidebar__sub">
        {list.itemCount} · {list.doneCount} done
      </span>
    </button>
  );
}
